import GetCalendarResult from 'src/models/accounting-calendar/GetCalendarResult';

export const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

/**
 * Quarter-end close months (1-based): March, June, September, December — the fiscal quarter-end
 * months. Their close runs in the following calendar month (e.g. June's QEC runs in July) using
 * the longer 10-working-day window. All other months use the normal 7-day MEC window.
 */
const QUARTER_END_CLOSE_MONTHS = [3, 6, 9, 12];

/**
 * Timezone the accounting calendar operates in. The backend anchors "today", the close windows,
 * and the current year to US Pacific (America/Los_Angeles), so the label must resolve "today" in
 * the same zone — not the viewer's local zone — otherwise a non-Pacific user near midnight would
 * see the wrong accounting day (e.g. "Day 2" locally when it's still "Day 1" in Pacific).
 */
const ACCOUNTING_TIME_ZONE = 'America/Los_Angeles';

/** Today's date in the accounting timezone (US Pacific), formatted as "YYYY-MM-DD". */
export function accountingTodayIso(today: Date = new Date()): string {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: ACCOUNTING_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(today);
}

/**
 * Derives the dynamic MEC-day label from the calendar response by looking up today's date
 * (in US Pacific, the accounting zone) and reading its mecDay value.
 *
 * - Positive mecDay (in close window): we are closing the PRIOR month, so the label shows the
 *   prior month's name and its close type. e.g. "Day 8 of June QEC" when today is July 13.
 * - Negative mecDay (post-close): we are past the close window, so the label shows the CURRENT
 *   month's name and its close type. e.g. "Day -9 of July MEC" when today is July 22.
 *
 * QEC applies when the month being referenced is a fiscal quarter end (Mar/Jun/Sep/Dec).
 * Returns '' when today's MEC day is unavailable (non-working day, or data not loaded).
 *
 * @param calendar the GET /calendar response.
 * @param today    the instant to resolve; defaults to now. (Injectable for testing.)
 */
export function deriveMecDayLabel(calendar: GetCalendarResult | null | undefined, today: Date = new Date()): string {
    if (!calendar || !calendar.months) {
        return '';
    }
    // Resolve "today" in the accounting timezone (Pacific), formatted as YYYY-MM-DD (en-CA).
    const todayIso = accountingTodayIso(today);
    const monthKey = todayIso.slice(0, 7); // "YYYY-MM"
    const month = Number(todayIso.slice(5, 7));
    const monthDays = calendar.months[monthKey] || [];
    const todayData = monthDays.find((d) => d.date === todayIso);
    if (!todayData || todayData.mecDay === undefined || todayData.mecDay === null) {
        return '';
    }

    if (todayData.mecDay > 0) {
        // In the close window: we are closing the PRIOR month.
        const priorMonth = month === 1 ? 12 : month - 1;
        const closeType = QUARTER_END_CLOSE_MONTHS.includes(priorMonth) ? 'QEC' : 'MEC';
        return `Day ${todayData.mecDay} of ${MONTH_NAMES[priorMonth - 1]} ${closeType}`;
    } else {
        // Post-close (negative mecDay): current month context.
        const closeType = QUARTER_END_CLOSE_MONTHS.includes(month) ? 'QEC' : 'MEC';
        return `Day ${todayData.mecDay} of ${MONTH_NAMES[month - 1]} ${closeType}`;
    }
}

/*
* Example outputs:
*   Day 18 (July MEC)        (due date in August; July close, non-QEC month)
*   Day 5  (March QEC)       (due date in April; March close, calendar Q1 end)
*   Day 3  (December QEC)    (due date in January; December close, calendar Q4 end)
*   'N/A'                    (dueDateWorkDayNumber or dueDateDisplay missing)
*/
export function formatAccountingDueDate(
    dueDateWorkDayNumber: number | null | undefined,
    dueDateDisplay: string | null | undefined
): string {
    if (dueDateWorkDayNumber == null || !dueDateDisplay) {
        return 'N/A';
    }
    const parts = dueDateDisplay.split('-');
    if (parts.length < 2) {
        return `Day ${dueDateWorkDayNumber}`;
    }
    const dueMonth = Number(parts[1]); // 1-based, e.g. "03" → 3
    if (isNaN(dueMonth) || dueMonth < 1 || dueMonth > 12) {
        return `Day ${dueDateWorkDayNumber}`;
    }
    // closingMonth = one calendar month before the due-date's month (Jan closes December).
    const closingMonth = dueMonth === 1 ? 12 : dueMonth - 1;
    const closeType = QUARTER_END_CLOSE_MONTHS.includes(closingMonth) ? 'QEC' : 'MEC';
    return `Day ${dueDateWorkDayNumber} (${MONTH_NAMES[closingMonth - 1]} ${closeType})`;
}